import type { Allocation } from '@/entities/allocations/allocation.entity';
import type { Booking } from '@/entities/bookings/booking.entity';
import { VEHICLE_INSPECTION_FLAG } from '@/components/inspections/inspection-state';
import { useFeatureFlagSnapshot } from '@/hooks/use-feature-flag';
import { getOperationalStationOfCar } from '@/hooks/use-current-station-of-car';
import { useYieldManagementSettings } from '@/queries/use-yield-management-settings';
import { isLongTermPricing } from '@/utils/pricing.utils';
import { ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';

/** Read once when the handover can be decided. Later booking or settings edits do not move the step. */
export function useFrozenHandoverInspectionRequired(
  booking: MaybeRefOrGetter<Booking | undefined>,
  allocation: MaybeRefOrGetter<Allocation | undefined>,
): { required: Ref<boolean>; isReady: Ref<boolean> } {
  const rollout = useFeatureFlagSnapshot(VEHICLE_INSPECTION_FLAG);
  const { data: yieldSettings, isSuccess: yieldSettingsLoaded } = useYieldManagementSettings();
  const required = ref(false);
  const isReady = ref(false);
  watch(
    () => ({
      rolloutLoaded: rollout.isLoaded.value,
      rolloutEnabled: rollout.isEnabled.value,
      booking: toValue(booking),
      allocation: toValue(allocation),
      yieldSettingsLoaded: yieldSettingsLoaded.value,
      requireInspectionStationIds: yieldSettings.value?.requireInspectionStationIds,
    }),
    (value) => {
      if (isReady.value || !value.rolloutLoaded || !value.booking) {
        return;
      }
      if (!value.rolloutEnabled) {
        required.value = false;
        isReady.value = true;
        return;
      }
      if (isLongTermPricing(value.booking.priceCalculation)) {
        required.value = true;
        isReady.value = true;
        return;
      }
      if (!value.allocation || !value.yieldSettingsLoaded) {
        return;
      }
      const handingOverStationId =
        value.allocation.pickupResponsibleStation?.id ??
        getOperationalStationOfCar(value.allocation.car, value.allocation.startDate).id;
      required.value = (value.requireInspectionStationIds ?? []).includes(handingOverStationId);
      isReady.value = true;
    },
    { immediate: true },
  );
  return { required, isReady };
}
